import React, { useState, useEffect } from "react";
import { Empty, Spin } from "antd";
import { Link } from "react-router-dom";
import {
  SearchOutlined,
  ArrowRightOutlined,
  FileTextOutlined,
  SafetyOutlined,
  CustomerServiceOutlined,
  BookOutlined,
  CheckCircleOutlined,
  ReadOutlined,
  FireOutlined,
  TagOutlined,
} from "@ant-design/icons";
import Header from "../../components/Header";
import "./style.css";

const Help = () => {
  const [activeCategory, setActiveCategory] = useState("全部");
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoaded, setIsLoaded] = useState(false);
  const [tutorials, setTutorials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 15; // 3列 × 5行

  // 获取教程数据
  const fetchTutorials = async (category = null) => {
    setLoading(true);
    try {
      const response = await fetch('https://hui51.cn/api/graphql', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          operationName: 'guidesWithCount',
          query: `
            query guidesWithCount($type: String, $cat: String, $_search: String, $limit: String, $skip: String) {
              guidesWithCount(type: $type, cat: $cat, _search: $_search, limit: $limit, skip: $skip, sort: "order") {
                total
                guides {
                  _id
                  title
                  type
                  cat
                  order
                }
              }
            }
          `,
          variables: {
            type: 'html',
            ...(category && category !== "全部" ? { cat: category } : {}),
            limit: '1000',
            skip: 0
          }
        })
      });

      const data = await response.json();
      if (data.data && data.data.guidesWithCount) {
        setTutorials(data.data.guidesWithCount.guides.filter(g => g.cat !== '疫情防控'));
      }
    } catch (error) {
      console.error('获取教程数据失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 获取所有分类
  const fetchCategories = async () => {
    try {
      const response = await fetch('https://hui51.cn/api/graphql', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          operationName: 'guidesWithCount',
          query: `
            query guidesWithCount($type: String, $cat: String, $_search: String, $limit: String, $skip: String) {
              guidesWithCount(type: $type, cat: $cat, _search: $_search, limit: $limit, skip: $skip, sort: "order") {
                total
                guides {
                  cat
                }
              }
            }
          `,
          variables: {
            type: 'html',
            limit: '1000',
            skip: 0
          }
        })
      });

      const data = await response.json();
      if (data.data && data.data.guidesWithCount) {
        const uniqueCategories = [...new Set(data.data.guidesWithCount.guides.map(g => g.cat).filter(Boolean))].filter(cat => cat !== '疫情防控');
        setCategories(uniqueCategories);
      }
    } catch (error) {
      console.error('获取分类失败:', error);
    }
  };

  // 搜索教程
  const searchTutorials = async (query) => {
    setLoading(true);
    try {
      const response = await fetch('https://hui51.cn/api/graphql', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          operationName: 'guidesWithCount',
          query: `
            query guidesWithCount($type: String, $cat: String, $_search: String, $limit: String, $skip: String) {
              guidesWithCount(type: $type, cat: $cat, _search: $_search, limit: $limit, skip: $skip, sort: "order") {
                total
                guides {
                  _id
                  title
                  type
                  cat
                  order
                }
              }
            }
          `,
          variables: {
            type: 'html',
            ...(activeCategory !== "全部" ? { cat: activeCategory } : {}),
            _search: query,
            limit: '100',
            skip: 0
          }
        })
      });

      const data = await response.json();
      if (data.data && data.data.guidesWithCount) {
        setTutorials(data.data.guidesWithCount.guides.filter(g => g.cat !== '疫情防控'));
      }
    } catch (error) {
      console.error('搜索教程失败:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setIsLoaded(true);
    fetchCategories();
    fetchTutorials();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isLoaded) {
      setCurrentPage(1);
      fetchTutorials(activeCategory);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategory]);

  useEffect(() => {
    if (isLoaded && searchQuery) {
      setCurrentPage(1);
      const timeoutId = setTimeout(() => {
        searchTutorials(searchQuery);
      }, 300);
      return () => clearTimeout(timeoutId);
    } else if (isLoaded && !searchQuery) {
      fetchTutorials(activeCategory);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  // 分类配置
  const categoryConfig = {
    "全部": { icon: <ReadOutlined />, color: "#2563eb" },
    "操作指南": { icon: <FileTextOutlined />, color: "#2563eb" },
    "功能效果": { icon: <SafetyOutlined />, color: "#7c3aed" },
    "家校沟通": { icon: <CustomerServiceOutlined />, color: "#059669" },
    "模板合集": { icon: <BookOutlined />, color: "#dc2626" },
    "接龙相关": { icon: <CheckCircleOutlined />, color: "#ea580c" },
  };

  // 获取分类图标
  const getCategoryIcon = (cat) => {
    return categoryConfig[cat]?.icon || <TagOutlined />;
  };

  // 获取分类颜色
  const getCategoryColor = (cat) => {
    return categoryConfig[cat]?.color || "#2563eb";
  };

  // 分页计算
  const totalPages = Math.ceil(tutorials.length / PAGE_SIZE);
  const paginatedTutorials = tutorials.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }
    if (start > 1) {
      pages.push(1);
      if (start > 2) pages.push('...');
    }
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    if (end < totalPages) {
      if (end < totalPages - 1) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  return (
    <div className={`help-page ${isLoaded ? "loaded" : ""}`}>
      <Header activePath="/help" />

      {/* Hero Section */}
      <section className="help-hero">
        <div className="help-hero-bg">
          <div className="help-hero-shape shape-1" />
          <div className="help-hero-shape shape-2" />
          <div className="help-hero-shape shape-3" />
        </div>
        <div className="help-hero-content">
          <h1 className="help-hero-title">
            如何帮助您？
          </h1>
          <p className="help-hero-desc">
            搜索教程、浏览分类，快速找到您需要的帮助文档
          </p>
          <div className="help-hero-search">
            <div className="search-box">
              <SearchOutlined className="search-box-icon" />
              <input
                type="text"
                className="search-box-input"
                placeholder="搜索教程关键词..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button className="search-box-clear" onClick={() => setSearchQuery("")}>
                  ×
                </button>
              )}
              <button className="search-box-btn">
                搜索
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Category Tabs */}
      <section className="help-categories">
        <div className="help-categories-inner">
          <div className="category-tabs">
            <button
              className={`category-tab ${activeCategory === "全部" ? "active" : ""}`}
              onClick={() => setActiveCategory("全部")}
            >
              <span className="tab-icon" style={{ color: "#2563eb" }}>
                <ReadOutlined />
              </span>
              <span className="tab-label">全部</span>
            </button>
            {categories.map((category) => (
              <button
                key={category}
                className={`category-tab ${activeCategory === category ? "active" : ""}`}
                onClick={() => setActiveCategory(category)}
              >
                <span className="tab-icon" style={{ color: getCategoryColor(category) }}>
                  {getCategoryIcon(category)}
                </span>
                <span className="tab-label">{category}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Tutorial Grid */}
      <section className="help-tutorials">
        <div className="help-tutorials-inner">
          <div className="tutorials-header">
            <h2 className="tutorials-title">
              {searchQuery ? `「${searchQuery}」的搜索结果` : activeCategory === "全部" ? "全部教程" : activeCategory}
            </h2>
            <span className="tutorials-count">
              共 {tutorials.length} 篇
            </span>
          </div>

          {loading ? (
            <div className="tutorials-loading">
              <Spin size="large" />
            </div>
          ) : tutorials.length > 0 ? (
            <>
              <div className="tutorials-grid">
                {paginatedTutorials.map((tutorial, index) => (
                  <Link
                    key={tutorial._id}
                    to={`/HelpDetail/${tutorial._id}`}
                    className="tutorial-card-new"
                    style={{ animationDelay: `${index * 0.04}s` }}
                  >
                    <div className="tutorial-card-icon" style={{ backgroundColor: `${getCategoryColor(tutorial.cat)}15`, color: getCategoryColor(tutorial.cat) }}>
                      {getCategoryIcon(tutorial.cat)}
                    </div>
                    <div className="tutorial-card-body">
                      <div className="tutorial-card-tag" style={{ color: getCategoryColor(tutorial.cat), backgroundColor: `${getCategoryColor(tutorial.cat)}10` }}>
                        {tutorial.cat}
                      </div>
                      <h3 className="tutorial-card-title">{tutorial.title}</h3>
                    </div>
                    <div className="tutorial-card-arrow">
                      <ArrowRightOutlined />
                    </div>
                  </Link>
                ))}
              </div>
              {totalPages > 1 && (
                <div className="pagination">
                  <button
                    className="pagination-btn pagination-prev"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  >
                    ‹
                  </button>
                  {getPageNumbers().map((page, idx) => (
                    page === '...' ? (
                      <span key={`ellipsis-${idx}`} className="pagination-ellipsis">…</span>
                    ) : (
                      <button
                        key={page}
                        className={`pagination-btn pagination-num ${currentPage === page ? 'active' : ''}`}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </button>
                    )
                  ))}
                  <button
                    className="pagination-btn pagination-next"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  >
                    ›
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="tutorials-empty">
              <Empty
                description={searchQuery ? "未找到相关教程" : "该分类暂无教程"}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            </div>
          )}
        </div>
      </section>

      {/* CTA - 与首页一致 */}
      <section className="cta">
        <div className="container">
          <div className="cta-content">
            <h2>现在就试试秒应</h2>
            <p>扫码立即体验，免费使用，无需注册</p>
            <div className="cta-qr-codes">
              <div className="qr-item">
                <img src="/assets/code1.8137c5d.jpg" alt="微信小程序码" className="qr-img" />
                <span className="qr-label">微信小程序</span>
              </div>
              <div className="qr-item">
                <img src="/assets/code4.6658546.jpg" alt="网页版二维码" className="qr-img" />
                <span className="qr-label">微信公众号</span>
              </div>
              <div className="qr-item">
                <img src="/assets/code3.563ecfb.jpg" alt="客服微信" className="qr-img" />
                <span className="qr-label">客服微信：jizhitech</span>
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Help;
